import { useEffect, useState } from 'react';
import type { AdminUserResponse } from '../../types/api';
import { inputClass } from '../ui/inputClass';

interface Props {
  open: boolean;
  user: AdminUserResponse | null;
  onClose: () => void;
  onSave: (body: { email?: string; newPassword?: string; unlock?: boolean }) => Promise<void>;
}

export function EditUserDialog({ open, user, onClose, onSave }: Props) {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [unlock, setUnlock] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(user?.email ?? '');
      setNewPassword('');
      setUnlock(false);
      setError(null);
    }
  }, [open, user]);

  if (!open || !user) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: { email?: string; newPassword?: string; unlock?: boolean } = {};

    const trimmedEmail = email.trim();
    if (trimmedEmail && trimmedEmail !== user!.email) body.email = trimmedEmail;
    if (newPassword) body.newPassword = newPassword;
    if (unlock) body.unlock = true;

    if (!body.email && !body.newPassword && !body.unlock) {
      setError('Change the email, set a new password, or check unlock.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSave(body);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update user.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-sm rounded-xl bg-white dark:bg-slate-800 p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Edit User</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} className={inputClass} type="email" autoComplete="off" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">New password</label>
            <input value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputClass} type="text" autoComplete="off" placeholder="Leave blank to keep current" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={unlock} onChange={e => setUnlock(e.target.checked)} className="h-4 w-4" />
            Unlock account
          </label>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-md border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
